import React from "react";
import {
  Card,
  H1,
  H3,
  Text,
  Button,
  Tag,
  Spinner,
  NonIdealState,
  Icon,
  InputGroup,
} from "@blueprintjs/core";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { DateInput } from "@blueprintjs/datetime";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import { withRouter } from "react-router-dom";
import cogoToast from "cogo-toast";
import moment from "moment";
import { getDisplayStatusMeta as getDisplayStatusMetaGlobal } from "../../constants/statusColors";
import { getIsBilledLabel, getIsBilledPill } from "../../utils/isBilledPill";

class PendingSurgeryRegister extends React.Component {
  state = {
    rows: [],
    allRows: [],
    confirmedNotBilledCount: 0,
    dischargedNotBilledCount: 0,
    billedNotDischargedCount: 0,
    billedCancelledCount: 0,
    billedPostponedCount: 0,
    branches: [],
    doctors: [],
    isLoading: false,
    isGenerating: false,
    fromDate: new Date(),
    toDate: new Date(),
    selectedBranch: "ALL_BRANCHES",
    selectedBranchName: null,
    selectedDoctor: "",
    selectedDoctorName: null,
    quickFilterText: "",
    gridApi: null,
    gridColumnApi: null,
    activeBadgeFilter: null,
  };

  numericFields = [
    "Package",
    "Special Discount",
    "MOU Discount",
    "IOL Discount",
    "Total Receivable",
    "Authorisation",
    "Net Payable",
    "Total Collected",
    "Total Refund",
    "Net Collected",
    "Balance",
  ];

  componentDidMount = async () => {
    await this.loadInitialData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.authStore?.selectedBranchName !== this.props.authStore?.selectedBranchName) {
      const preferredName = this.props.authStore?.selectedBranchName;
      if (!preferredName) {
        this.setState({
          selectedBranch: "ALL_BRANCHES",
          selectedBranchName: null,
        });
        return;
      }
      const match = (this.state.branches || []).find(
        (branch) => (branch.name || "").toLowerCase() === String(preferredName).toLowerCase()
      );
      if (match && match._id !== this.state.selectedBranch) {
        this.setState({
          selectedBranch: match._id,
          selectedBranchName: match.name,
        });
      }
    }
  }

  loadInitialData = async () => {
    try {
      this.setState({ isLoading: true });
      const [branches] = await Promise.all([this.loadBranches(), this.loadDoctors()]);

      if (Array.isArray(branches) && branches.length) {
        const preferredName = this.props.authStore?.selectedBranchName;
        const normalizedPreferredName = String(preferredName || "")
          .trim()
          .toLowerCase();
        const matchedBranch = branches.find(
          (branch) => String(branch.name || "").trim().toLowerCase() === normalizedPreferredName
        );

        this.setState({
          selectedBranch: matchedBranch?._id || "ALL_BRANCHES",
          selectedBranchName: matchedBranch?.name || null,
        });
      }

      await this.generateReport();
    } catch (err) {
      console.error("Error loading Pending Surgery Register:", err);
      cogoToast.error("Failed to load Pending Surgery Register");
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});
      const rawBranches = response?.data || response || [];
      const branches = (Array.isArray(rawBranches) ? rawBranches : [])
        .map((site, idx) => ({
          _id:
            site?._id ||
            site?.id ||
            site?.siteId ||
            site?.value ||
            site?.key ||
            (typeof site === "string" ? site : String(idx)),
          name:
            site?.site ||
            site?.siteName ||
            site?.name ||
            site?.branchName ||
            site?.label ||
            (typeof site === "string" ? site : `Site ${idx + 1}`),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      this.setState({ branches });
      return branches;
    } catch (err) {
      console.error("Error loading branches:", err);
      try {
        const fallbackResponse = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/branchMaster/getAll`, {});
        const fallbackRawBranches = fallbackResponse?.data || fallbackResponse || [];
        const fallbackBranches = (Array.isArray(fallbackRawBranches) ? fallbackRawBranches : [])
          .map((site, idx) => ({
            _id: site?._id || site?.id || String(idx),
            name:
              site?.site ||
              site?.siteName ||
              site?.name ||
              site?.branchName ||
              site?.label ||
              `Site ${idx + 1}`,
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ branches: fallbackBranches });
        return fallbackBranches;
      } catch (fallbackErr) {
        console.error("Fallback branch API failed:", fallbackErr);
        const sampleBranches = [
          { _id: "branch1", name: "Barrackpore" },
          { _id: "branch2", name: "Kolkata" },
        ];
        this.setState({ branches: sampleBranches });
        return sampleBranches;
      }
    }
  };

  loadDoctors = async () => {
    try {
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});
      const doctors = Array.isArray(response)
        ? response.map((consultant) => ({
          _id: consultant._id || consultant.id,
          name: consultant.name || consultant.consultantName,
          shortcode: consultant.shortcode || consultant.code,
        }))
        : [];
      this.setState({ doctors });
      return doctors;
    } catch (err) {
      console.error("Error loading doctors:", err);
      try {
        const fallbackResponse = await apiCallPost(`/meta-sx/${this.props.authStore.prefix}/doctorMaster/getAll`, {});
        const fallbackDoctors = fallbackResponse?.data || fallbackResponse || [];
        this.setState({ doctors: fallbackDoctors });
        return fallbackDoctors;
      } catch (fallbackErr) {
        console.error("Fallback doctor API failed:", fallbackErr);
        const sampleDoctors = [{ _id: "doc1", name: "DR. ARNAB DAS" }];
        this.setState({ doctors: sampleDoctors });
        return sampleDoctors;
      }
    }
  };

  getNumber = (...candidates) => {
    for (const candidate of candidates) {
      if (typeof candidate === "number" && !Number.isNaN(candidate)) return candidate;
      if (
        typeof candidate === "string" &&
        candidate.trim() !== "" &&
        !Number.isNaN(Number(candidate))
      ) {
        return Number(candidate);
      }
    }
    return 0;
  };

  getLatestAuthorization = (item) => {
    const entries = item?.authorization?.entries || [];
    if (!Array.isArray(entries) || entries.length === 0) return null;

    const activeIndex = item?.authorization?.activeIndex;
    if (typeof activeIndex === "number" && activeIndex >= 0 && activeIndex < entries.length) {
      return entries[activeIndex];
    }

    return [...entries].sort((a, b) => new Date(b?.ts || 0) - new Date(a?.ts || 0))[0];
  };

  getLatestInvoice = (item) => {
    const invoices = item?.invoices || [];
    if (!Array.isArray(invoices) || invoices.length === 0) return null;
    return [...invoices].sort((a, b) => new Date(b?.generatedAt || 0) - new Date(a?.generatedAt || 0))[0];
  };

  getDisplayStatusMeta = (item) => {
    const status = item?.status || item?.bookingStatus || item?.workflowStatus || "-";
    const payorPanel = item?.payorPanel || item?.payor || "";
    const isBilled = item?.isBilled ?? false;
    return getDisplayStatusMetaGlobal({ status, payorPanel, isBilled });
  };

  renderStatusCell = (item) => {
    const meta = this.getDisplayStatusMeta(item || {});
    return `<span style="display:inline-flex;align-items:center;justify-content:center;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;color:${meta.color};background:${meta.background};border:1px solid ${meta.border};text-transform:none;line-height:1;">${meta.label}</span>`;
  };

  renderIsBilledCell = (item) => getIsBilledPill(item);

  formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    return `₹${numericValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  mapItemToRow = (item) => {
    const latestInvoice = this.getLatestInvoice(item);
    const latestAuthorization = this.getLatestAuthorization(item);
    const consolidatedBilling = latestInvoice?.consolidatedBilling || item?.consolidatedBilling || {};

    const surgeryRatePaise = this.getNumber(consolidatedBilling?.surgeryRatePaise);
    const anaestheticChargesPaise = this.getNumber(consolidatedBilling?.anaestheticChargesPaise);
    const tissueProcessingPaise = this.getNumber(consolidatedBilling?.tissueProcessingPaise);
    const femtoChargesPaise = this.getNumber(consolidatedBilling?.femtoChargesPaise);
    const specialDiscountPaise = this.getNumber(consolidatedBilling?.specialDiscountPaise);
    const mouDiscountPaise = this.getNumber(consolidatedBilling?.mouDiscountPaise);
    const iolDiscountPaise = this.getNumber(consolidatedBilling?.iolDiscountPaise);

    const packageAmount = Math.max(0, (surgeryRatePaise + anaestheticChargesPaise + tissueProcessingPaise) / 100);
    const femtoAmount = Math.max(0, femtoChargesPaise / 100);
    const specialDiscount = Math.max(0, specialDiscountPaise / 100);
    const mouDiscount = Math.max(0, mouDiscountPaise / 100);
    const iolDiscount = Math.max(0, iolDiscountPaise / 100);

    const totalReceivable = Math.max(
      0,
      packageAmount + femtoAmount - specialDiscount - mouDiscount - iolDiscount
    );
    const authorisation = Math.max(0, this.getNumber(latestAuthorization?.amountRupees));
    const netPayable = Math.max(0, totalReceivable - authorisation);

    const totalCollected = this.getNumber(item?.payments?.totalPaid, item?.payments?.totalPaidPaise / 100);
    const totalRefund = this.getNumber(item?.payments?.totalRefunded, item?.payments?.totalRefundedPaise / 100);
    const netCollected = totalCollected - totalRefund;
    const balance = netPayable - netCollected;

    const statusMeta = this.getDisplayStatusMeta(item);
    const statusRaw = String(item?.status || item?.bookingStatus || item?.workflowStatus || "").trim().toLowerCase();
    const doctorNameRaw = item?.doctorName || "-";

    return {
      _id: item?._id || item?.id || item?.bookingReference || "-",
      _surgeryDateRaw: item?.scheduledDate || item?.createdAt || null,
      _source: item,
      statusRaw,
      doctorNameRaw,
      isBilledRaw: Boolean(item?.isBilled),
      Branch: item?.branchName || "-",
      "Surgery ID": item?.bookingReference || item?._id || "-",
      "Surgery Date": item?.scheduledDate ? moment(item.scheduledDate).format("DD-MMM-YYYY") : "-",
      PID: item?.patientGenericPid || item?.patientId || item?.patientOriginalData?.sysPid || "-",
      "Patient Name": item?.patientName || item?.patientOriginalData?.name || "-",
      "Doctor/Surgeon": doctorNameRaw ? doctorNameRaw.replace(/^(Dr\.|DR\.)\s*/i, "") : "-",
      Eye: item?.selectedSurgeries?.[0]?.laterality || item?.eye || item?.laterality || "-",
      Status: statusMeta.label || "-",
      "Is Billed": getIsBilledLabel(item),
      Payor: item?.payorPanel || item?.payor || "-",
      Package: packageAmount,
      "Special Discount": specialDiscount,
      "MOU Discount": mouDiscount,
      "IOL Discount": iolDiscount,
      "Total Receivable": totalReceivable,
      Authorisation: authorisation,
      "Net Payable": netPayable,
      "Total Collected": totalCollected,
      "Total Refund": totalRefund,
      "Net Collected": netCollected,
      Balance: balance,
      "Billed By": item?.markedBilledBy?.name || latestInvoice?.generatedBy?.name || "-",
    };
  };

  isConfirmedRow = (row) => {
    return String(row?.statusRaw || "").toLowerCase() === "confirmed";
  };

  isDischargedRow = (row) => {
    const status = String(row?.statusRaw || "")
      .trim()
      .toLowerCase();
    return status.includes("discharg");
  };

  isPostponedRow = (row) => {
    const status = String(row?.statusRaw || "")
      .trim()
      .toLowerCase();
    return status.includes("postpon");
  };

  isCancelledRow = (row) => {
    const status = String(row?.statusRaw || "")
      .trim()
      .toLowerCase();
    return status.includes("cancel");
  };

  isEligibleRow = (row) => {
    const isBilledPostponed = row?.isBilledRaw && this.isPostponedRow(row);
    const isBilledCancelled = row?.isBilledRaw && this.isCancelledRow(row);
    return (
      this.isConfirmedRow(row) ||
      (this.isDischargedRow(row) && !row?.isBilledRaw) ||
      isBilledPostponed ||
      isBilledCancelled
    );
  };

  sortRowsBySurgeryDate = (rows) => {
    return [...rows].sort((a, b) => {
      const dateA = a?._surgeryDateRaw ? new Date(a._surgeryDateRaw).getTime() : 0;
      const dateB = b?._surgeryDateRaw ? new Date(b._surgeryDateRaw).getTime() : 0;
      return dateB - dateA;
    });
  };

  generateReport = async () => {
    try {
      this.setState({ isGenerating: true });

      const payload = {
        reportType: "MASTER",
        startDate: moment(this.state.fromDate).format("YYYY-MM-DD"),
        endDate: moment(this.state.toDate).format("YYYY-MM-DD"),
        page: 1,
        limit: 30000,
        noLimit: true,
      };

      if (this.state.selectedBranch && this.state.selectedBranch !== "ALL_BRANCHES") {
        const selectedBranch = this.state.branches.find((branch) => branch._id === this.state.selectedBranch);
        if (selectedBranch?.name) {
          payload.branch = selectedBranch.name;
        }
      }

      const endpoint = `/v3/${this.props.authStore.prefix}/reports/ipdBillRegister`;
      const response = await apiCallPost(endpoint, payload);
      const items = response?.data?.items || [];
      const mappedRows = (Array.isArray(items) ? items : []).map((item) => this.mapItemToRow(item));
      const confirmedNotBilledRows = mappedRows.filter(
        (row) => this.isConfirmedRow(row) && !row.isBilledRaw
      );
      const dischargedNotBilledRows = mappedRows.filter(
        (row) => this.isDischargedRow(row) && !row.isBilledRaw
      );
      const billedPostponedRows = mappedRows.filter(
        (row) => row.isBilledRaw && this.isPostponedRow(row)
      );
      const billedCancelledRows = mappedRows.filter(
        (row) => row.isBilledRaw && this.isCancelledRow(row)
      );
      const billedNotDischargedRows = mappedRows.filter(
        (row) =>
          row.isBilledRaw &&
          !this.isDischargedRow(row) &&
          !this.isPostponedRow(row) &&
          !this.isCancelledRow(row)
      );

      this.setState(
        {
          allRows: mappedRows,
        },
        () => {
          this.applyClientSideFilters();
        }
      );

      cogoToast.success(
        `Loaded ${confirmedNotBilledRows.length} confirmed (not billed) • ${dischargedNotBilledRows.length} discharged (not billed) • ${billedNotDischargedRows.length} billed (not discharged) • ${billedCancelledRows.length} billed (cancelled) • ${billedPostponedRows.length} billed (postponed)`
      );
    } catch (err) {
      console.error("Error generating Pending Surgery Register:", err);
      cogoToast.error("Failed to generate Pending Surgery Register");
      this.setState({
        allRows: [],
        rows: [],
        confirmedNotBilledCount: 0,
        dischargedNotBilledCount: 0,
        billedNotDischargedCount: 0,
        billedCancelledCount: 0,
        billedPostponedCount: 0,
      });
    } finally {
      this.setState({ isGenerating: false });
    }
  };

  createBranchSelectItems = () => this.state.branches.map((branch) => branch.name).filter(Boolean);

  createDoctorSelectItems = () => {
    return this.state.doctors
      .map((doctor) => {
        const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
        return `${shortcode}${doctor.name}`;
      })
      .filter(Boolean);
  };

  getFormattedDoctorName = (doctor) => {
    if (!doctor) return null;
    const shortcode = doctor.shortcode ? `${doctor.shortcode} | ` : "";
    return `${shortcode}${doctor.name}`;
  };

  extractDoctorNameFromFormatted = (formattedDoctorName) => {
    if (!formattedDoctorName) return null;
    const doctorNameWithoutCode = formattedDoctorName.includes(" | ")
      ? formattedDoctorName.split(" | ")[1]
      : formattedDoctorName;
    return doctorNameWithoutCode.trim();
  };

  handleBranchFilterSelect = (branchName) => {
    if (branchName === null || branchName === undefined) {
      this.setState({
        selectedBranch: "ALL_BRANCHES",
        selectedBranchName: null,
      });
      return;
    }

    const selectedBranch = this.state.branches.find((branch) => branch.name === branchName);
    this.setState({
      selectedBranch: selectedBranch ? selectedBranch._id : "ALL_BRANCHES",
      selectedBranchName: branchName || null,
    });
  };

  handleDoctorFilterSelect = (formattedDoctorName) => {
    const selectedDoctor = this.state.doctors.find((doctor) => this.getFormattedDoctorName(doctor) === formattedDoctorName);
    this.setState(
      {
        selectedDoctor: selectedDoctor ? selectedDoctor._id : "",
        selectedDoctorName: formattedDoctorName || null,
      },
      () => {
        this.applyClientSideFilters();
      }
    );
  };

  getRowsAfterDoctorFilter = (rows) => {
    const { selectedDoctor, selectedDoctorName } = this.state;
    let filteredRows = [...rows];

    if (selectedDoctor && selectedDoctorName) {
      const selectedDoctorNameOnly = this.extractDoctorNameFromFormatted(selectedDoctorName);
      filteredRows = filteredRows.filter((row) => {
        const rowDoctor = String(row.doctorNameRaw || "").trim();
        const targetDoctor = String(selectedDoctorNameOnly || "").trim();
        const rowDoctorWithoutPrefix = rowDoctor.replace(/^DR\.\s*/i, "");
        const targetDoctorWithoutPrefix = targetDoctor.replace(/^DR\.\s*/i, "");

        return (
          rowDoctor.toLowerCase() === targetDoctor.toLowerCase() ||
          rowDoctorWithoutPrefix.toLowerCase() === targetDoctorWithoutPrefix.toLowerCase() ||
          rowDoctor.toLowerCase().includes(targetDoctor.toLowerCase())
        );
      });
    }

    return filteredRows;
  };

  applyClientSideFilters = () => {
    const filteredRows = this.getRowsAfterDoctorFilter(this.state.allRows || []);

    const confirmedNotBilledRows = filteredRows.filter(
      (row) => this.isConfirmedRow(row) && !row.isBilledRaw
    );
    const dischargedNotBilledRows = filteredRows.filter(
      (row) => this.isDischargedRow(row) && !row.isBilledRaw
    );
    const billedPostponedRows = filteredRows.filter(
      (row) => row.isBilledRaw && this.isPostponedRow(row)
    );
    const billedCancelledRows = filteredRows.filter(
      (row) => row.isBilledRaw && this.isCancelledRow(row)
    );
    const billedNotDischargedRows = filteredRows.filter(
      (row) =>
        row.isBilledRaw &&
        !this.isDischargedRow(row) &&
        !this.isPostponedRow(row) &&
        !this.isCancelledRow(row)
    );
    const displayRows = this.state.activeBadgeFilter
      ? this.filterRowsByBadge(filteredRows, this.state.activeBadgeFilter)
      : filteredRows.filter(this.isEligibleRow);
    const sortedDisplayRows = this.sortRowsBySurgeryDate(displayRows);

    this.setState({
      rows: sortedDisplayRows,
      confirmedNotBilledCount: confirmedNotBilledRows.length,
      dischargedNotBilledCount: dischargedNotBilledRows.length,
      billedNotDischargedCount: billedNotDischargedRows.length,
      billedCancelledCount: billedCancelledRows.length,
      billedPostponedCount: billedPostponedRows.length,
    });
  };

  handleBadgeClick = (filterKey) => {
    this.setState(
      (prevState) => ({
        activeBadgeFilter: prevState.activeBadgeFilter === filterKey ? null : filterKey,
      }),
      () => {
        this.applyClientSideFilters();
      }
    );
  };

  filterRowsByBadge = (rows, filterKey) => {
    switch (filterKey) {
      case "confirmed_not_billed":
        return rows.filter((row) => this.isConfirmedRow(row) && !row.isBilledRaw);
      case "discharged_not_billed":
        return rows.filter((row) => this.isDischargedRow(row) && !row.isBilledRaw);
      case "billed_not_discharged":
        return rows.filter(
          (row) =>
            row.isBilledRaw &&
            !this.isDischargedRow(row) &&
            !this.isPostponedRow(row) &&
            !this.isCancelledRow(row)
        );
      case "billed_cancelled":
        return rows.filter((row) => row.isBilledRaw && this.isCancelledRow(row));
      case "billed_postponed":
        return rows.filter((row) => row.isBilledRaw && this.isPostponedRow(row));
      default:
        return rows.filter(this.isEligibleRow);
    }
  };

  navigateToDetails = (rowData) => {
    const bookingId = rowData?._id || rowData?.["Surgery ID"];
    if (!bookingId || bookingId === "-") return;
    window.open(`/v3/workflow/surgery-booking/${bookingId}`, "_blank", "noopener,noreferrer");
  };

  getColumnDefs = () => [
    {
      headerName: "Branch",
      field: "Branch",
      width: 140,
      pinned: "left",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Surgery ID",
      field: "Surgery ID",
      width: 130,
      pinned: "left",
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        const value = params.value;
        if (!value || value === "-") return "-";
        const container = document.createElement("div");
        const link = document.createElement("span");
        link.innerHTML = value;
        link.style.color = "#137cbd";
        link.style.cursor = "pointer";
        link.style.textDecoration = "underline";
        link.style.fontWeight = "600";
        link.onclick = () => this.navigateToDetails(params.data);
        container.appendChild(link);
        return container;
      },
    },
    {
      headerName: "Surgery Date",
      field: "Surgery Date",
      width: 130,
      filter: "agTextColumnFilter",
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "PID",
      field: "PID",
      width: 130,
      filter: "agTextColumnFilter",
      cellStyle: { fontWeight: "600" },
    },
    {
      headerName: "Patient Name",
      field: "Patient Name",
      width: 190,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Doctor/Surgeon",
      field: "Doctor/Surgeon",
      width: 170,
      filter: "agTextColumnFilter",
      cellStyle: { fontWeight: "600" },
    },
    {
      headerName: "Eye",
      field: "Eye",
      width: 90,
      filter: "agSetColumnFilter",
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Status",
      field: "Status",
      width: 140,
      filter: "agSetColumnFilter",
      filterValueGetter: (params) => this.getDisplayStatusMeta(params.data?._source || {}).label,
      valueFormatter: (params) => this.getDisplayStatusMeta(params.data?._source || {}).label,
      cellRenderer: (params) => this.renderStatusCell(params.data?._source || {}),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Is Billed",
      field: "Is Billed",
      width: 120,
      filter: "agSetColumnFilter",
      valueGetter: (params) => getIsBilledLabel(params.data?._source || params.data),
      valueFormatter: (params) => getIsBilledLabel(params.data?._source || params.data),
      cellRenderer: (params) => this.renderIsBilledCell(params.data?._source || params.data),
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Payor",
      field: "Payor",
      width: 130,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Package",
      field: "Package",
      width: 130,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Special Discount",
      field: "Special Discount",
      width: 150,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "MOU Discount",
      field: "MOU Discount",
      width: 140,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "IOL Discount",
      field: "IOL Discount",
      width: 130,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Total Receivable",
      field: "Total Receivable",
      width: 150,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Authorisation",
      field: "Authorisation",
      width: 140,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Net Payable",
      field: "Net Payable",
      width: 140,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Total Collected",
      field: "Total Collected",
      width: 150,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Total Refund",
      field: "Total Refund",
      width: 130,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Net Collected",
      field: "Net Collected",
      width: 140,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Balance",
      field: "Balance",
      width: 130,
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => this.formatCurrency(params.value),
      cellStyle: { textAlign: "right", fontWeight: "600" },
    },
    {
      headerName: "Billed By",
      field: "Billed By",
      width: 150,
      filter: "agTextColumnFilter",
    },
  ];

  onGridReady = (params) => {
    this.setState({
      gridApi: params.api,
      gridColumnApi: params.columnApi,
    });
  };

  getDisplayedColumns = () => {
    if (!this.state.gridColumnApi) return this.getColumnDefs();
    const allColumns = this.state.gridColumnApi.getAllColumns();
    if (!allColumns) return this.getColumnDefs();
    const visibleColumns = allColumns.filter((column) => column.isVisible());
    return visibleColumns.map((column) => {
      const columnDef = column.getColDef();
      return {
        headerName: columnDef.headerName,
        field: columnDef.field,
        ...columnDef,
      };
    });
  };

  getSortedAndFilteredData = () => {
    if (!this.state.gridApi) return this.state.rows || [];
    const data = [];
    this.state.gridApi.forEachNodeAfterFilterAndSort((node) => data.push(node.data));
    return data;
  };

  getExportValue = (row, field) => {
    if (field === "Status") {
      return this.getDisplayStatusMeta(row?._source || {}).label || "-";
    }
    if (field === "Is Billed") {
      return getIsBilledLabel(row?._source || row);
    }
    if (this.numericFields.includes(field)) {
      return this.formatCurrency(row?.[field] || 0);
    }
    return row?.[field] ?? "-";
  };

  exportToCSV = () => {
    const rows = this.getSortedAndFilteredData();
    const columns = this.getDisplayedColumns();
    const headers = columns.map((column) => column.headerName);
    const dataRows = rows.map((row) =>
      columns.map((column) => {
        const value = String(this.getExportValue(row, column.field) ?? "");
        return `"${value.replace(/"/g, '""')}"`;
      })
    );

    const csvLines = [headers.join(","), ...dataRows.map((row) => row.join(","))];
    const csvContent = csvLines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `pending-surgery-register-${moment().format("YYYYMMDD-HHmm")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    cogoToast.success("CSV exported successfully");
  };

  exportToPDF = () => {
    const rows = this.getSortedAndFilteredData();
    const columns = this.getDisplayedColumns();

    const selectedBranchLabel =
      this.state.selectedBranch === "ALL_BRANCHES"
        ? "All Branches"
        : this.state.branches.find((branch) => branch._id === this.state.selectedBranch)?.name || "All Branches";
    const selectedDoctorLabel = this.state.selectedDoctorName
      ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
      : "All Doctors";

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      cogoToast.error("Popup blocked. Allow popups to export PDF");
      return;
    }

    const html = `
            <!doctype html>
            <html>
                <head>
                    <meta charset="utf-8" />
                    <title>Pending Surgery Register</title>
                    <style>
                        @page { size: A4 landscape; margin: 8mm; }
                        body { font-family: Arial, sans-serif; margin: 0; padding: 8px; color: #111; }
                        h1 { margin: 0 0 8px 0; font-size: 18px; }
                        .meta { margin-bottom: 10px; color: #444; font-size: 12px; }
                        table { width: 100%; border-collapse: collapse; font-size: 10px; }
                        th, td { border: 1px solid #aaa; padding: 4px; text-align: left; vertical-align: top; }
                        th { background: #efefef; text-align: center; }
                    </style>
                </head>
                <body>
                    <h1>PENDING SURGERY REGISTER</h1>
                    <div class="meta">
                        ${selectedBranchLabel} • ${selectedDoctorLabel} • ${moment(this.state.fromDate).format(
      "DD-MMM-YYYY"
    )} to ${moment(this.state.toDate).format("DD-MMM-YYYY")} • ${rows.length} records
                    </div>
                    <table>
                        <thead>
                            <tr>${columns.map((column) => `<th>${column.headerName}</th>`).join("")}</tr>
                        </thead>
                        <tbody>
                            ${rows
        .map(
          (row) =>
            `<tr>${columns
              .map((column) => `<td>${this.getExportValue(row, column.field)}</td>`)
              .join("")}</tr>`
        )
        .join("")}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    cogoToast.success("PDF opened for printing");
  };

  handleDateChange = (field, date) => {
    this.setState({ [field]: date });
  };

  onQuickFilterChanged = (event) => {
    this.setState({ quickFilterText: event.target.value });
  };

  renderHeader = () => {
    const selectedBranchLabel =
      this.state.selectedBranch === "ALL_BRANCHES"
        ? "All Branches"
        : this.state.branches.find((branch) => branch._id === this.state.selectedBranch)?.name || "All Branches";
    const selectedDoctorLabel = this.state.selectedDoctorName
      ? this.extractDoctorNameFromFormatted(this.state.selectedDoctorName)
      : "All Doctors";

    return (
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          borderBottom: "2px solid var(--border-color)",
          paddingBottom: "12px",
        }}
      >
        <H1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "24px" }}>
          PENDING SURGERY REGISTER - {this.props.authStore?.hospitalName || "DISHA EYE HOSPITALS PRIVATE LIMITED"}
        </H1>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "15px" }}>
          {selectedBranchLabel} • {selectedDoctorLabel} • {moment(this.state.fromDate).format("DD-MMM-YYYY")} to{" "}
          {moment(this.state.toDate).format("DD-MMM-YYYY")}
        </Text>
        <Text style={{ color: "var(--text-secondary)", margin: "5px 0", fontSize: "13px" }}>
          Showing surgeries in confirmed status, discharged but not billed, billed cancelled, or billed postponed
        </Text>
      </div>
    );
  };

  renderFilters = () => {
    return (
      <Card
        elevation={2}
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
          <Icon icon="filter" style={{ marginRight: "10px", color: "var(--text-primary)" }} />
          <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Filters</H3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap", marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              From
            </Tag>
            <DateInput
              value={this.state.fromDate}
              onChange={(date) => this.handleDateChange("fromDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(value) => moment(value, "DD-MMM-YYYY").toDate()}
              placeholder="From Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              To
            </Tag>
            <DateInput
              value={this.state.toDate}
              onChange={(date) => this.handleDateChange("toDate", date)}
              formatDate={(date) => moment(date).format("DD-MMM-YYYY")}
              parseDate={(value) => moment(value, "DD-MMM-YYYY").toDate()}
              placeholder="To Date"
              style={{ width: "140px" }}
              rightElement={<Icon icon="calendar" size={14} />}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Branch
            </Tag>
            <div style={{ width: "170px" }}>
              <SingleSelectWrapper
                items={[`All Branches (${this.state.branches.length})`, ...this.createBranchSelectItems()]}
                selectedItem={this.state.selectedBranchName || `All Branches (${this.state.branches.length})`}
                placeholder="Search branches..."
                onChange={(value) => {
                  if (value === `All Branches (${this.state.branches.length})`) {
                    this.handleBranchFilterSelect(null);
                  } else {
                    this.handleBranchFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Doctor/Surgeon
            </Tag>
            <div style={{ width: "190px" }}>
              <SingleSelectWrapper
                items={[`All Doctors (${this.state.doctors.length})`, ...this.createDoctorSelectItems()]}
                selectedItem={this.state.selectedDoctorName || `All Doctors (${this.state.doctors.length})`}
                placeholder="Search doctors..."
                onChange={(value) => {
                  if (value === `All Doctors (${this.state.doctors.length})`) {
                    this.handleDoctorFilterSelect(null);
                  } else {
                    this.handleDoctorFilterSelect(value);
                  }
                }}
                width="100%"
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Tag minimal style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
              Quick Filter
            </Tag>
            <InputGroup
              placeholder="Search all columns..."
              value={this.state.quickFilterText}
              onChange={this.onQuickFilterChanged}
              leftIcon="search"
              style={{ width: "280px" }}
              small
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button intent="primary" icon="search" onClick={this.generateReport} loading={this.state.isGenerating}>
              Generate
            </Button>
            <Button icon="document" onClick={this.exportToPDF} disabled={!this.state.rows.length}>
              PDF
            </Button>
            <Button icon="th" onClick={this.exportToCSV} disabled={!this.state.rows.length}>
              CSV
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  getBadgeStyle = (filterKey) => {
    const { activeBadgeFilter } = this.state;
    const isActive = activeBadgeFilter === filterKey;
    const hasActiveFilter = activeBadgeFilter !== null;

    if (isActive) {
      return {
        cursor: "pointer",
        boxShadow: "0 0 0 2px rgba(255,255,255,0.9), 0 2px 8px rgba(0,0,0,0.3)",
        transform: "scale(1.05)",
        transition: "all 0.15s ease",
        userSelect: "none",
      };
    }

    return {
      cursor: "pointer",
      opacity: hasActiveFilter ? 0.5 : 1,
      transition: "all 0.15s ease",
      userSelect: "none",
    };
  };

  renderCountBoxes = () => {
    const { activeBadgeFilter } = this.state;

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
        <Tag
          intent="success"
          large
          interactive
          onClick={() => this.handleBadgeClick("confirmed_not_billed")}
          style={this.getBadgeStyle("confirmed_not_billed")}
        >
          {this.state.confirmedNotBilledCount} Confirmed, not Billed
        </Tag>
        <Tag
          intent="warning"
          large
          interactive
          onClick={() => this.handleBadgeClick("discharged_not_billed")}
          style={this.getBadgeStyle("discharged_not_billed")}
        >
          {this.state.dischargedNotBilledCount} Discharged, not Billed
        </Tag>
        <Tag
          intent="primary"
          large
          interactive
          onClick={() => this.handleBadgeClick("billed_not_discharged")}
          style={this.getBadgeStyle("billed_not_discharged")}
        >
          {this.state.billedNotDischargedCount} Billed, not Discharged
        </Tag>
        <Tag
          intent="none"
          large
          interactive
          onClick={() => this.handleBadgeClick("billed_cancelled")}
          style={this.getBadgeStyle("billed_cancelled")}
        >
          {this.state.billedCancelledCount} Billed, Cancelled
        </Tag>
        <Tag
          intent="danger"
          large
          interactive
          onClick={() => this.handleBadgeClick("billed_postponed")}
          style={this.getBadgeStyle("billed_postponed")}
        >
          {this.state.billedPostponedCount} Billed, Postponed
        </Tag>
        {activeBadgeFilter && (
          <Tag
            minimal
            large
            interactive
            icon="cross"
            onClick={() => this.handleBadgeClick(activeBadgeFilter)}
            style={{ cursor: "pointer", marginLeft: "4px" }}
          >
            Clear Filter
          </Tag>
        )}
      </div>
    );
  };

  renderTable = () => {
    if (this.state.isGenerating) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Generating Report..."
          description="Please wait while we fetch confirmed, discharged-not-billed, billed-cancelled, and billed-postponed surgeries"
        />
      );
    }

    if (!this.state.rows || this.state.rows.length === 0) {
      return (
        <NonIdealState
          icon="endorsed"
          title="No Matching Surgeries Found"
          description="No surgeries found in confirmed status, discharged but not billed, billed cancelled, or billed postponed, for the selected filters."
        />
      );
    }

    return (
      <div
        className="ag-theme-alpine-dark"
        style={{
          height: "620px",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <AgGridReact
          rowData={this.state.rows}
          columnDefs={this.getColumnDefs()}
          onGridReady={this.onGridReady}
          quickFilterText={this.state.quickFilterText}
          animateRows={true}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          enableCellTextSelection={true}
          sideBar={{
            toolPanels: [
              {
                id: "columns",
                labelDefault: "Columns",
                labelKey: "columns",
                iconKey: "columns",
                toolPanel: "agColumnsToolPanel",
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressColumnFilter: false,
                  suppressColumnSelectAll: false,
                  suppressColumnExpandAll: false,
                },
              },
              {
                id: "filters",
                labelDefault: "Filters",
                labelKey: "filters",
                iconKey: "filter",
                toolPanel: "agFiltersToolPanel",
                toolPanelParams: {
                  suppressExpandAll: false,
                  suppressFilterSearch: false,
                },
              },
            ],
            defaultToolPanel: null,
            hiddenByDefault: false,
            position: "right",
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 80,
            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
          }}
          sortingOrder={["desc", "asc"]}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          suppressPaginationPanel={false}
          domLayout="normal"
          getRowId={(params) => params.data._id || params.data["Surgery ID"]}
          rowHeight={48}
          headerHeight={40}
        />
      </div>
    );
  };

  render() {
    if (this.state.isLoading) {
      return <NonIdealState icon={<Spinner />} title="Loading..." description="Preparing filters and data" />;
    }

    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Icon icon="clipboard" style={{ marginRight: "12px", color: "var(--intent-primary)" }} />
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Pending Surgery Register</H1>
          </div>
        </div>

        {this.renderFilters()}

        <Card
          elevation={2}
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-surface)",
            border: "1px solid var(--border-color)",
          }}
        >
          {this.renderHeader()}
          {this.renderCountBoxes()}
          {this.renderTable()}
        </Card>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(PendingSurgeryRegister)));
